import _ from "lodash";

/**
 * Note: This will return an unsigned "0" if the number is 0.
 * @param {Number}
 * @return {String}
 */
function signedNumber(n) {
  if (n > 0) {
    return "+" + n;
  } else {
    return n.toString();
  }
}

/**
 * Splits a chunk of text into lines, such that 1) we don't break words, and 2) no line exceeds a
 * given number of characters.
 * @param {String} A chunk of text without newlines, where words are separated by spaces.
 * @param {Number} How many characters to allow on each line.
 * @return {Array<String>}
 */
function wrapText(text, characterLimit) {
  const result = [];
  const words = text.split(" ");

  let currentLine = [];
  _.forEach(words, (word) => {
    if (currentLine.concat(word).join(" ").length > characterLimit) {
      // If adding this word would make the line too long, deposit what we have (if anything) in the
      // results and move on to a new line.

      if (currentLine.length > 0) {
        result.push(currentLine.join(" "));
      }
      currentLine = [];
    }

    currentLine.push(word);
  });

  // Tack on the words that were hanging around in currentLine when we finished.
  result.push(currentLine.join(" "));

  return result;
}

/**
 * Sorts an array of strings alphabetically, except for certain enumerated strings which are put at
 * the end. An example use case: alphabetically sorting an array that contains 'Other', but you want
 * 'Other' to be at the end.
 * @param {Array<String>} The strings to sort.
 * @param {Array<String>} The list of exceptional strings to put at the end of the sorted array.
 * @return {Array<String>} Most strings sorted alphabetically, exceptional strings sorted according
 *     to the order they appear in the exceptions array.
 */
function sortedAlphabeticalWithExceptions(strings, exceptions) {
  const unexceptionalStrings = strings.filter((string) => !exceptions.includes(string));
  const exceptionalStrings = strings.filter((string) => exceptions.includes(string));

  const sortedUnexceptionalStrings = [...unexceptionalStrings].sort();
  const sortedExceptionalStrings = [...exceptionalStrings].sort(
    (a, b) => exceptions.indexOf(a) - exceptions.indexOf(b)
  );
  return sortedUnexceptionalStrings.concat(sortedExceptionalStrings);
}

/**
 * @param {String} The first name of the user.
 * @param {String} The last name of the user.
 * @return {String} The combined and formatted name of the user.
 */
function fullDisplayNameFor(firstName, lastName) {
  return _.upperFirst(firstName) + " " + _.upperFirst(lastName);
}

/**
 * @param {String} The phrase to parameterize.
 * @return {String} The parameterized phrase.
 */
function parameterize(phrase) {
  return phrase
    .trim()
    .toLowerCase()
    .replace(/[^a-zA-Z0-9 -]/, "")
    .replace(/\s/g, "-");
}

const emailRegex = /^\w+([.-]?\w+)*(\+([.-]?\w+)*)?@\w+([.-]?\w+)*(\.\w{2,3})+/;

// 10 or more characters and contain at least 1 uppercase character, 1 number, and 1 special character.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const passwordRegex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[`~!@#$%^&*()\-=_+{}\[\]|\\;:'",<.>\/?])(?=.{10,})/;
const passwordRegexMismatchMessage =
  "Must be 10 or more characters and contain at least 1 uppercase character, 1 number, and 1 special character.";

export {
  signedNumber,
  wrapText,
  sortedAlphabeticalWithExceptions,
  fullDisplayNameFor,
  parameterize,
  emailRegex,
  passwordRegex,
  passwordRegexMismatchMessage,
};
